import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Lock, LogOut, Calendar, FileText, Copy, Check, Trash2, RefreshCw } from "lucide-react";
import { api, API } from "@/lib/api";

const TOKEN_KEY = "shine_admin_token";

function useAdminToken() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const save = (t) => { localStorage.setItem(TOKEN_KEY, t); setToken(t); };
  const clear = () => { localStorage.removeItem(TOKEN_KEY); setToken(""); };
  return { token, save, clear };
}

function fmtDate(iso) { try { return new Date(iso).toLocaleString("en-NZ"); } catch { return iso; } }

function LoginCard({ onLogin }) {
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!pw) return;
    setLoading(true);
    try {
      const { data } = await api.post("/admin/login", { password: pw });
      onLogin(data.token);
      toast.success("Signed in");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6">
      <Card className="w-full max-w-md rounded-3xl border-gray-100 shadow-sm" data-testid="admin-login-card">
        <CardContent className="p-8">
          <div className="w-12 h-12 rounded-2xl bg-[#0EA5E9]/10 flex items-center justify-center mb-4">
            <Lock className="w-5 h-5 text-[#0EA5E9]" />
          </div>
          <h1 className="font-heading text-2xl font-bold">Admin sign-in</h1>
          <p className="mt-1 text-sm text-[#4B5563]">Internal access only.</p>
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="admin-login-form">
            <div className="space-y-1.5">
              <Label htmlFor="admin-pw">Password</Label>
              <Input id="admin-pw" data-testid="admin-input-password" type="password" value={pw} onChange={(e) => setPw(e.target.value)} autoFocus />
            </div>
            <Button type="submit" disabled={loading} className="w-full rounded-full bg-[#0369A1] hover:bg-[#0284C7] py-6" data-testid="admin-submit">
              {loading ? "Signing in..." : "Sign in"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function ICSPanel() {
  const url = `${API}/bookings/calendar.ics`;
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("ICS link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Copy failed - copy manually"); }
  };
  return (
    <Card className="rounded-3xl border-gray-100 shadow-sm" data-testid="ics-panel">
      <CardContent className="p-6">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-[#0EA5E9]" />
          <h2 className="font-heading text-xl font-bold">Google Calendar feed</h2>
        </div>
        <p className="mt-2 text-sm text-[#4B5563]">Subscribe in Google Calendar to see every booking sync automatically (updates every few hours).</p>
        <div className="mt-4 flex gap-2">
          <Input value={url} readOnly className="font-mono text-xs" data-testid="ics-url" />
          <Button type="button" onClick={copy} variant="outline" className="rounded-full" data-testid="ics-copy">
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </Button>
        </div>
        <div className="mt-4 rounded-xl bg-[#F3F4F6] p-4 text-xs text-[#4B5563] leading-relaxed">
          <div className="font-semibold text-[#111827] mb-1">How to subscribe in Google Calendar</div>
          <ol className="list-decimal pl-4 space-y-1">
            <li>Open <a className="underline" href="https://calendar.google.com" target="_blank" rel="noreferrer">calendar.google.com</a> on desktop.</li>
            <li>In the left sidebar, click <strong>+</strong> next to &quot;Other calendars&quot; → <strong>From URL</strong>.</li>
            <li>Paste the link above and click <strong>Add calendar</strong>.</li>
            <li>Done - bookings appear on your calendar with customer name, address, phone & notes.</li>
          </ol>
        </div>
      </CardContent>
    </Card>
  );
}

function QuotesList({ token, onUnauth }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/quotes", { headers: { Authorization: `Bearer ${token}` } });
      setItems(data);
    } catch (err) {
      if (err?.response?.status === 401) onUnauth();
      else toast.error("Failed to load quotes");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this quote?")) return;
    try {
      await api.delete(`/admin/quotes/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Deleted");
      load();
    } catch { toast.error("Couldn't delete"); }
  };

  return (
    <div className="space-y-4" data-testid="admin-quotes-list">
      <div className="flex items-center justify-between">
        <div className="text-sm text-[#4B5563]">{items.length} total quote{items.length === 1 ? "" : "s"}</div>
        <Button type="button" variant="outline" size="sm" onClick={load} className="rounded-full" data-testid="admin-quotes-refresh">
          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
        </Button>
      </div>
      {loading ? (
        <div className="text-sm text-[#4B5563]">Loading...</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-[#4B5563] rounded-2xl bg-[#F3F4F6] p-6 text-center">No quote requests yet.</div>
      ) : (
        <div className="space-y-3">
          {items.map((q) => (
            <Card key={q.id} className="rounded-2xl border-gray-100" data-testid={`quote-${q.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-heading text-lg font-bold">{q.name}</div>
                      <Badge variant="secondary" className="bg-[#E0F2FE] text-[#0369A1] hover:bg-[#E0F2FE]">
                        {q.service.replace("_", " ")}
                      </Badge>
                      {q.estimated_price ? (
                        <Badge className="bg-[#F59E0B] hover:bg-[#F59E0B] text-[#111827]">NZ${q.estimated_price.toFixed(2)}</Badge>
                      ) : (
                        <Badge variant="outline">Measure-up</Badge>
                      )}
                    </div>
                    <div className="mt-2 grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-[#4B5563]">
                      <div><span className="text-[#111827]">Phone:</span> <a className="hover:underline" href={`tel:${q.phone}`}>{q.phone}</a></div>
                      <div><span className="text-[#111827]">Email:</span> {q.email || "-"}</div>
                      <div className="sm:col-span-2"><span className="text-[#111827]">Address:</span> {q.address}</div>
                      <div className="sm:col-span-2"><span className="text-[#111827]">SQM:</span> {q.knows_sqm ? `${q.sqm} sqm` : "Asked us to measure up"}</div>
                      {q.notes && <div className="sm:col-span-2"><span className="text-[#111827]">Notes:</span> {q.notes}</div>}
                      <div className="sm:col-span-2 text-xs">Received: {fmtDate(q.created_at)}</div>
                    </div>
                  </div>
                  <Button type="button" variant="ghost" size="icon" onClick={() => del(q.id)} className="text-red-600 hover:bg-red-50 hover:text-red-700" data-testid={`quote-delete-${q.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function BookingCardItem({ b, onDelete }) {
  return (
    <Card key={b.id} className="rounded-2xl border-gray-100" data-testid={`booking-${b.id}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="font-heading text-lg font-bold">{b.booking_date}</div>
              <Badge className={b.slot === "morning" ? "bg-[#0369A1] hover:bg-[#0369A1]" : "bg-[#D97706] hover:bg-[#D97706]"}>{b.slot}</Badge>
              <Badge variant="secondary" className="bg-[#F3F4F6] text-[#111827]">{b.service.replace("_", " ")}</Badge>
            </div>
            <div className="mt-2 grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-[#4B5563]">
              <div><span className="text-[#111827]">Customer:</span> {b.name}</div>
              <div><span className="text-[#111827]">Phone:</span> <a className="hover:underline" href={`tel:${b.phone}`}>{b.phone}</a></div>
              <div><span className="text-[#111827]">Email:</span> {b.email || "-"}</div>
              <div className="sm:col-span-2"><span className="text-[#111827]">Address:</span> {b.address}</div>
              {b.notes && <div className="sm:col-span-2"><span className="text-[#111827]">Notes:</span> {b.notes}</div>}
              <div className="sm:col-span-2 text-xs">Booked: {fmtDate(b.created_at)}</div>
            </div>
          </div>
          <Button type="button" variant="ghost" size="icon" onClick={() => onDelete(b.id)} className="text-red-600 hover:bg-red-50 hover:text-red-700" data-testid={`booking-delete-${b.id}`}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function BookingsList({ token, onUnauth }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/bookings", { headers: { Authorization: `Bearer ${token}` } });
      setItems(data);
    } catch (err) {
      if (err?.response?.status === 401) onUnauth();
      else toast.error("Failed to load bookings");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Cancel/delete this booking?")) return;
    try {
      await api.delete(`/admin/bookings/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Deleted");
      load();
    } catch { toast.error("Couldn't delete"); }
  };

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = items.filter((b) => b.booking_date >= today);
  const past = items.filter((b) => b.booking_date < today);

  return (
    <div className="space-y-6" data-testid="admin-bookings-list">
      <div className="flex items-center justify-between">
        <div className="text-sm text-[#4B5563]">{upcoming.length} upcoming · {past.length} past</div>
        <Button type="button" variant="outline" size="sm" onClick={load} className="rounded-full" data-testid="admin-bookings-refresh">
          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
        </Button>
      </div>
      {loading && <div className="text-sm text-[#4B5563]">Loading...</div>}
      {!loading && (
        <>
          <div>
            <div className="font-heading text-sm uppercase tracking-widest text-[#0369A1] mb-3">Upcoming</div>
            {upcoming.length === 0 ? <div className="text-sm text-[#4B5563] rounded-2xl bg-[#F3F4F6] p-6 text-center">No upcoming bookings.</div> : (
              <div className="space-y-3">{upcoming.map((b) => <BookingCardItem key={b.id} b={b} onDelete={del} />)}</div>
            )}
          </div>
          {past.length > 0 && (
            <div>
              <div className="font-heading text-sm uppercase tracking-widest text-[#4B5563] mb-3">Past</div>
              <div className="space-y-3 opacity-70">{past.map((b) => <BookingCardItem key={b.id} b={b} onDelete={del} />)}</div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function Admin() {
  const { token, save, clear } = useAdminToken();

  if (!token) return <LoginCard onLogin={save} />;

  return (
    <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-12">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#0EA5E9] font-semibold">Admin</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#111827]">Quotes &amp; Bookings</h1>
        </div>
        <Button type="button" variant="outline" onClick={clear} className="rounded-full" data-testid="admin-signout">
          <LogOut className="w-4 h-4 mr-2" /> Sign out
        </Button>
      </div>

      <div className="mb-8"><ICSPanel /></div>

      <Tabs defaultValue="bookings" className="w-full">
        <TabsList className="rounded-full bg-[#F3F4F6] p-1" data-testid="admin-tabs">
          <TabsTrigger value="bookings" className="rounded-full data-[state=active]:bg-white" data-testid="admin-tab-bookings"><Calendar className="w-4 h-4 mr-2" />Bookings</TabsTrigger>
          <TabsTrigger value="quotes" className="rounded-full data-[state=active]:bg-white" data-testid="admin-tab-quotes"><FileText className="w-4 h-4 mr-2" />Quotes</TabsTrigger>
        </TabsList>
        <TabsContent value="bookings" className="mt-6"><BookingsList token={token} onUnauth={clear} /></TabsContent>
        <TabsContent value="quotes" className="mt-6"><QuotesList token={token} onUnauth={clear} /></TabsContent>
      </Tabs>
    </div>
  );
}
